// Phase 1A Phase 9A.10 — Signal provenance helper.
//
// Pure deterministic copy bundle for the "Data sources" drawer section.
// Names every field-family on a Market Signal Card and explains where
// it comes from today. NO React, NO Supabase, NO browser APIs, NO
// clock reads, NO side effects, NO mutation of shared state.
//
// Truth contract:
//   - Each item is signal-invariant — provenance is a property of the
//     pipeline, not of any one fixture.
//   - Vocabulary is deliberate: "Phase-1A cache" / "seeded" / "computed"
//     instead of "dummy" / "mock" / "placeholder". The product is
//     genuinely deterministic Phase-1A scaffolding, not a fake demo.
//   - Live integrations the product does NOT have are denied by the
//     final "Not connected in Phase 1A" item — never claimed.
//   - Future API-Football / live-odds / agent-runtime work flips
//     individual `kind` values from "not_connected" → "fetched" via a
//     future signature extension. Today the function takes no args.
//
// Function inventory:
//   - getProvenanceItems()  → readonly array of 8 ProvenanceItem rows

export type ProvenanceKind =
  | "fetched" // from a Supabase row
  | "computed" // from a pure helper at render time
  | "stored" // a stored field, not a runtime calculation
  | "user_state" // per-user state (e.g. watchlist)
  | "not_connected"; // explicit denial of a feed STRIKZA does not have

export interface ProvenanceItem {
  label: string;
  body: string;
  kind: ProvenanceKind;
}

export function getProvenanceItems(): ProvenanceItem[] {
  // Returns a fresh array each call so callers can map / sort without
  // accidentally mutating shared module state.
  return [
    {
      label: "Fixture metadata",
      kind: "fetched",
      body: "Fixture rows are read from the Phase-1A cache. Today's pool is seeded; API-Football fetches arrive in a later phase.",
    },
    {
      label: "Implied probability",
      kind: "computed",
      body: "Computed from the chain's odds target — 100 ÷ odds-target. Not a live bookmaker quote.",
    },
    {
      label: "Baseline probability",
      kind: "fetched",
      body: "Read from the win-rate-by-odds reference table for this odds tier. Exact-match lookup; no nearest-tier fallback.",
    },
    {
      label: "Estimated edge",
      kind: "computed",
      body: "Computed live as baseline − implied. Not CLV. No closing-line tracking in Phase 1A.",
    },
    {
      label: "Exploitability score",
      kind: "stored",
      body: "Stored on the fixture row. A Phase-1A scaffold value, not a runtime model output.",
    },
    {
      label: "Tradability proxy",
      kind: "computed",
      body: "Computed live as league-tier-weight × exploitability-score. Not market liquidity, not order-book depth.",
    },
    {
      label: "Watchlist state",
      kind: "user_state",
      body: "Read from your private watchlist. Soft-delete via timestamp; no notifications, no alerts.",
    },
    {
      label: "Not connected in Phase 1A",
      kind: "not_connected",
      body: "No live bookmaker feed. No live match state. No model inference. No agent reasoning. No result feed. No CLV. No crowd sentiment.",
    },
  ];
}
